import { useState, useEffect, useCallback, useRef } from 'react';
import { MasaSiparisi, TrendyolOrder } from '../types';
import type { BlacklistCheckResult } from '../services/customerBlacklistService';

// ============================================================================
// NEW ORDER QUEUE HOOK — Sipariş Bildirim Kuyruğu (FIFO + Reactive Promotion)
// ============================================================================
//
// Telefon kuyruğunun (usePhoneCalls — activeCall + pendingCalls[]) BİREBİR
// PARALELİ. Yemek platformlarından (Getir, YS, Migros, Trendyol, QR, vs.)
// gelen yeni sipariş bildirimlerinin "tek slot overwrite" bug'ını çözmek için.
//
// KATI FIFO: yeni gelen sipariş ASLA mevcut active'i veya daha eski pending'leri
// atlamaz. Her şey önce kuyruğa girer, sonra koşullar uygunsa promotion olur.
//
// REACTIVE PROMOTION: ekran müsait olur olmaz pending[0] (en eski) otomatik
// active'e yükselir, NewOrderModal açılır, geri sayım başlar, ses çalar.
//
// EKRAN MÜSAİT TANIMI (caller'dan gelir — isScreenAvailable):
//   - anyModalOpen false (kullanıcı modalı yok, karar modalı dahil)
//   - activeCall null (telefon ne çalıyor ne konuşuluyor)
//   - activeNewOrder null (başka sipariş modal'ı zaten yok)
//
// BİRLEŞİK FIFO: phoneQueueEarliestTs ile telefon kuyruğundaki en eski
// bekleyen aramadan SONRA gelen yemek siparişleri promote edilmez.
// ============================================================================

export interface QueuedNewOrder {
  masaSiparisi: MasaSiparisi | null;
  trendyolOrder: TrendyolOrder | null;
  platform: 'qr' | 'ecommerce' | 'kiosk' | 'getir' | 'migros' | 'yemeksepeti' | 'trendyol';
  enqueuedAt: number;
  /**
   * Duplicate guard için benzersiz anahtar.
   * Örnekler: "ys_12345", "getir_g-abc", "trd_pkg-xyz", "qr_<siparisId>"
   */
  key: string;
  /** Kara liste kontrolü sürüyor: sonuç gelene kadar sipariş active'e yükselmez (otomatik onay erken başlamasın). */
  blacklistPending?: boolean;
  /** Kara liste kontrolünün sonucu (kesin ya da olası eşleşme). */
  blacklist?: BlacklistCheckResult | null;
}

export interface UseNewOrderQueueOptions {
  /** Ekran müsait mi (caller useMemo ile hesaplar) — true ise pending[0] promote olur */
  isScreenAvailable: boolean;
  /**
   * BİRLEŞİK FIFO (telefon + platform): telefon kuyruğundaki en eski bekleyen
   * aramanın startedAt timestamp'i. null değilse ve pending[0] yemek siparişi
   * bu aramadan SONRA kuyruğa girdiyse promotion BLOKLANIR — önce telefondaki
   * müşteri işlenir (katı kronolojik sıra).
   */
  phoneQueueEarliestTs?: number | null;
  /** Ses tetikleyici — sadece active'e promote olduğunda çağrılır (FIFO ilk geçiş) */
  playNewOrderSound: () => void;
  /** Ses durdurucu — active null olduğunda çağrılır */
  stopNewOrderSound: () => void;
}

export interface UseNewOrderQueueReturn {
  /** Şu an NewOrderModal'da gösterilen sipariş (null ise modal kapalı) */
  activeNewOrder: QueuedNewOrder | null;
  /** Sırada bekleyen siparişler (FIFO sıra: index 0 = en eski) */
  pendingNewOrders: QueuedNewOrder[];
  /** UI counter için (pendingNewOrders.length) */
  pendingCount: number;
  /**
   * Yeni sipariş bildirimi geldiğinde çağrılır. ASLA doğrudan active'e koymaz,
   * her zaman pending kuyruğun sonuna eklenir (FIFO). Reactive promotion useEffect
   * en kısa sürede uygun pending'i active'e yükseltir.
   *
   * @returns true → kuyruğa eklendi, false → duplicate (zaten varsa atlandı)
   */
  enqueueNewOrder: (q: QueuedNewOrder) => boolean;
  /**
   * Aktif sipariş işlemi bittiğinde (oto-onay+yazdırma veya manuel close).
   * Active'i null yapar, ses durdurur. Reactive promotion sıradaki pending'i alır.
   */
  completeActiveNewOrder: () => void;
  /** Alternatif: kullanıcı reddetti (semantik fark — şimdilik complete ile aynı) */
  dismissActiveNewOrder: () => void;
  /**
   * Bekleyen bir siparişi kuyruktan çıkarır (hiç active'e promote edilmeden).
   * Bildirim içindeki "Onayla ve Yazdır" akışı kullanır: sipariş bildirimden
   * onaylandığında NewOrderModal'ın bu sipariş için açılmaması gerekir.
   * seenKeysRef'ten KASITLI OLARAK silinmez — redelivery/polling aynı siparişi
   * tekrar kuyruğa sokamaz (completeActiveNewOrder ile aynı gerekçe).
   */
  removePendingNewOrder: (key: string) => void;
  /** Kuyruktaki (active ya da pending) siparişi günceller — kara liste sonucu sonradan gelir. */
  updateNewOrder: (key: string, patch: Partial<QueuedNewOrder>) => void;
  /** Closure-safe ref (handleAutoAcceptAndPrint snapshot için) */
  activeNewOrderRef: React.MutableRefObject<QueuedNewOrder | null>;
}

export const useNewOrderQueue = (options: UseNewOrderQueueOptions): UseNewOrderQueueReturn => {
  const { isScreenAvailable, phoneQueueEarliestTs = null, playNewOrderSound, stopNewOrderSound } = options;

  // ============================================================================
  // STATE — Dual: activeNewOrder + pendingNewOrders[]
  // ============================================================================

  const [activeNewOrder, setActiveNewOrderState] = useState<QueuedNewOrder | null>(null);
  const [pendingNewOrders, setPendingNewOrdersState] = useState<QueuedNewOrder[]>([]);

  // Closure-safe ref'ler — setter wrapper'lar bunları senkron tutar
  const activeNewOrderRef = useRef<QueuedNewOrder | null>(null);
  const pendingNewOrdersRef = useRef<QueuedNewOrder[]>([]);

  // Duplicate guard — aynı sipariş 2 kez kuyruğa girmesin
  // (SignalR aynı event'i 2 kez push edebilir, polling cycle'da tekrar gelebilir)
  const seenKeysRef = useRef<Set<string>>(new Set());

  // Reactive promotion guard — eş zamanlı çoklu setTimeout tetiklenmesin
  const promotionTimerRef = useRef<NodeJS.Timeout | null>(null);

  // ============================================================================
  // ATOMIC STATE SETTERS — state + ref senkron tutulur
  // ============================================================================

  const setActiveNewOrder = useCallback(
    (updater: QueuedNewOrder | null | ((prev: QueuedNewOrder | null) => QueuedNewOrder | null)) => {
      setActiveNewOrderState(prev => {
        const next = typeof updater === 'function'
          ? (updater as (p: QueuedNewOrder | null) => QueuedNewOrder | null)(prev)
          : updater;
        activeNewOrderRef.current = next;
        return next;
      });
    },
    [],
  );

  const setPendingNewOrders = useCallback(
    (updater: QueuedNewOrder[] | ((prev: QueuedNewOrder[]) => QueuedNewOrder[])) => {
      setPendingNewOrdersState(prev => {
        const next = typeof updater === 'function'
          ? (updater as (p: QueuedNewOrder[]) => QueuedNewOrder[])(prev)
          : updater;
        pendingNewOrdersRef.current = next;
        return next;
      });
    },
    [],
  );

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  const enqueueNewOrder = useCallback((q: QueuedNewOrder): boolean => {
    // Duplicate guard — anahtar zaten görüldüyse atla
    if (seenKeysRef.current.has(q.key)) {
      console.log('[NewOrderQueue] DUPLICATE atlandi:', q.key);
      return false;
    }

    // Aktif veya pending'de aynı key var mı? (paranoid check — seenKeysRef yetmeli ama yine de)
    if (activeNewOrderRef.current?.key === q.key) {
      console.log('[NewOrderQueue] DUPLICATE (active esit):', q.key);
      return false;
    }
    if (pendingNewOrdersRef.current.some(p => p.key === q.key)) {
      console.log('[NewOrderQueue] DUPLICATE (pending icerisinde):', q.key);
      return false;
    }

    console.log(
      '[NewOrderQueue] ENQUEUE:',
      q.key,
      '| platform:',
      q.platform,
      '| pending(before):',
      pendingNewOrdersRef.current.length,
      '| active:',
      activeNewOrderRef.current ? activeNewOrderRef.current.key : '(null)',
    );

    seenKeysRef.current.add(q.key);
    setPendingNewOrders(prev => [...prev, q]);
    return true;
  }, [setPendingNewOrders]);

  const completeActiveNewOrder = useCallback(() => {
    const ac = activeNewOrderRef.current;
    if (!ac) {
      console.log('[NewOrderQueue] completeActiveNewOrder cagrildi ama active yok');
      return;
    }

    console.log('[NewOrderQueue] COMPLETE active:', ac.key);
    // NOT: seenKeysRef'ten KASITLI OLARAK SİLMİYORUZ.
    // Aksi halde backend reconcile / network retry / SignalR redelivery aynı
    // sipariş ID'sini ikinci kez push ederse kuyruğa yeniden eklenir → modal
    // tekrar açılır → oto-onay tekrar tetiklenir → fiş İKİNCİ KEZ yazılır.
    // Set unmount sırasında zaten clear ediliyor (alt useEffect cleanup).
    // Bir vardiya boyunca max birkaç bin entry olur — bellek sorunu yok.
    setActiveNewOrder(null);
    stopNewOrderSound();
  }, [setActiveNewOrder, stopNewOrderSound]);

  const dismissActiveNewOrder = useCallback(() => {
    // Şimdilik complete ile aynı semantik — reddetme/iptal için ek API gerekirse
    // burada platform-spesifik reject çağrısı yapılabilir.
    completeActiveNewOrder();
  }, [completeActiveNewOrder]);

  const removePendingNewOrder = useCallback((key: string) => {
    const exists = pendingNewOrdersRef.current.some(p => p.key === key);
    if (!exists) {
      console.log('[NewOrderQueue] removePendingNewOrder: kuyrukta yok (muhtemelen promote edildi):', key);
      return;
    }
    console.log('[NewOrderQueue] REMOVE pending (bildirimden onaylandı):', key,
      '| remaining:', pendingNewOrdersRef.current.length - 1);
    // seenKeysRef'ten SİLMİYORUZ — aynı sipariş redelivery ile geri gelemez.
    setPendingNewOrders(prev => prev.filter(p => p.key !== key));
  }, [setPendingNewOrders]);

  const updateNewOrder = useCallback((key: string, patch: Partial<QueuedNewOrder>) => {
    if (activeNewOrderRef.current?.key === key) {
      setActiveNewOrder(prev => (prev && prev.key === key ? { ...prev, ...patch } : prev));
    }
    if (pendingNewOrdersRef.current.some(p => p.key === key)) {
      setPendingNewOrders(prev => prev.map(p => (p.key === key ? { ...p, ...patch } : p)));
    }
  }, [setActiveNewOrder, setPendingNewOrders]);

  // ============================================================================
  // REACTIVE PROMOTION — Ekran müsait olur olmaz pending[0] -> active
  // ============================================================================

  useEffect(() => {
    // Önceki bekleyen promotion'u iptal et (state burst değişikliklerinde çift tetiklenmesin)
    if (promotionTimerRef.current) {
      clearTimeout(promotionTimerRef.current);
      promotionTimerRef.current = null;
    }

    // BİRLEŞİK FIFO GATE: kuyrukta bekleyen bir telefon araması, sıradaki
    // yemek siparişinden ÖNCE geldiyse yemek promote edilmez — önce telefon
    // müşterisi işlenir (karar modalı üzerinden).
    const blockedByPhoneQueue =
      phoneQueueEarliestTs != null &&
      pendingNewOrders.length > 0 &&
      pendingNewOrders[0].enqueuedAt > phoneQueueEarliestTs;

    // Sadece şu dört koşul da sağlanırsa promotion yap:
    //  1. Active yok (modal kapalı)
    //  2. Pending'de en az 1 sipariş var
    //  3. Ekran müsait (caller useMemo ile hesaplıyor)
    //  4. Birleşik FIFO'da sıra yemekte (öncesinde bekleyen telefon yok)
    //  5. Sıradaki siparişin kara liste kontrolü bitti (otomatik onay erken başlamasın)
    const shouldPromote =
      activeNewOrder === null &&
      pendingNewOrders.length > 0 &&
      isScreenAvailable &&
      !blockedByPhoneQueue &&
      !pendingNewOrders[0].blacklistPending;

    if (!shouldPromote) return;

    // 100ms gecikme — modal kapanma animasyonu + state cleanup race koruması
    promotionTimerRef.current = setTimeout(() => {
      // Tekrar kontrol (timer içinde state değişmiş olabilir)
      if (activeNewOrderRef.current !== null) return;
      const next = pendingNewOrdersRef.current[0];
      if (!next || next.blacklistPending) return;

      console.log(
        '[NewOrderQueue] PROMOTE pending[0] -> active:',
        next.key,
        '| platform:',
        next.platform,
        '| remaining pending:',
        pendingNewOrdersRef.current.length - 1,
      );

      setPendingNewOrders(prev => prev.slice(1));
      setActiveNewOrder(next);
      playNewOrderSound();
      promotionTimerRef.current = null;
    }, 100);

    return () => {
      if (promotionTimerRef.current) {
        clearTimeout(promotionTimerRef.current);
        promotionTimerRef.current = null;
      }
    };
  }, [activeNewOrder, pendingNewOrders, isScreenAvailable, phoneQueueEarliestTs, setActiveNewOrder, setPendingNewOrders, playNewOrderSound]);

  // ============================================================================
  // CLEANUP — Unmount sırasında tüm timer'ları temizle
  // ============================================================================

  useEffect(() => {
    // Cleanup closure'a snapshot ile bağlanmasın diye ref'i fonksiyon
    // içinde lokal değişkene atıyoruz (eslint react-hooks/exhaustive-deps uyarısı).
    const keysSet = seenKeysRef.current;
    return () => {
      if (promotionTimerRef.current) {
        clearTimeout(promotionTimerRef.current);
        promotionTimerRef.current = null;
      }
      keysSet.clear();
    };
  }, []);

  return {
    activeNewOrder,
    pendingNewOrders,
    pendingCount: pendingNewOrders.length,
    enqueueNewOrder,
    completeActiveNewOrder,
    dismissActiveNewOrder,
    removePendingNewOrder,
    updateNewOrder,
    activeNewOrderRef,
  };
};
